import {useNavigation} from '@react-navigation/native';
import moment from 'moment';
import React, {useEffect, useMemo, useState} from 'react';
import {ScrollView, StyleSheet, Text, View} from 'react-native';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import {useSelector} from 'react-redux';
import {
  useGetDashboardsQuery,
  useGetDashboardRestaurantQuery,
} from '../../redux/services/dashboardApi';
import {useGetFOECheckinOccByDateQuery} from '../../redux/services/foecheckinApi';
import {useGetFOHighBalanceQuery} from '../../redux/services/foreportsApi';
import {RootState} from '../../redux/store';
import {
  colors,
  spacing,
  shadows,
  borderRadius,
  typography,
} from '../../theme/theme';

import T from '../../Components/T';
const num = (v: any) => Number(v) || 0;

type PnlLine = {
  label: string;
  key: string;
  color?: string;
  bold?: boolean;
  value?: (r: any) => number;
  show?: (rows: any[]) => boolean;
};

const hasPlan = (rows: any[]) => num(rows[0]?.rp_planlinks) > 0;
const hasNC = (rows: any[]) => rows.some(r => num(r.rp_ncsales) !== 0 || num(r.rp_nccount) !== 0);

const PNL_LINES: PnlLine[] = [
  {
    label: 'POS Sales',
    key: 'rp_possales',
    // older cached rows have no rp_possales
    value: r => (r.rp_possales !== undefined ? num(r.rp_possales) : num(r.rp_food) + num(r.rp_liquor)),
  },
  {label: 'Plan Sales', key: 'rp_plansales', show: hasPlan},
  {label: 'Plan Guests', key: 'rp_planpax', show: hasPlan},
  {label: 'NC Orders', key: 'rp_ncsales', show: hasNC},
  {label: 'Total Income', key: 'rp_income', color: colors.success, bold: true},
  {label: 'Store Issue', key: 'rp_storeissue'},
  {label: 'POS Issue', key: 'rp_posissue'},
  {label: 'Expense', key: 'rp_expense', color: colors.warning, bold: true},
  {label: 'Net', key: 'rp_net', bold: true},
];

// Rows = metrics, columns = Today / Week / Month
const RestaurantPnLCard = ({rows}: {rows: any[]}) => {
  const flagged = Number(rows[0]?.rp_flaggeddepts) || 0;
  return (
    <>
      <View style={localStyles.sectionHeader}>
        <View style={[localStyles.headerIconBadge, {backgroundColor: colors.warningLight}]}>
          <FontAwesome name="cutlery" size={16} color={colors.warning} />
        </View>
        <Text style={localStyles.sectionTitle}><T>Restaurant Income vs Expense</T></Text>
      </View>
      <View style={localStyles.summaryCard}>
        <View style={localStyles.tableHeaderRow}>
          <Text style={[localStyles.tableHeadCell, {flex: 1.4}]}> </Text>
          <Text style={[localStyles.tableHeadCell, localStyles.textRight]}><T>Today</T></Text>
          <Text style={[localStyles.tableHeadCell, localStyles.textRight]}><T>Week</T></Text>
          <Text style={[localStyles.tableHeadCell, localStyles.textRight]}><T>Month</T></Text>
        </View>
        {PNL_LINES.filter(line => !line.show || line.show(rows)).map((line, i) => (
          <View
            key={line.key}
            style={[localStyles.tableRow, i % 2 === 1 && localStyles.tableRowAlt]}>
            <Text style={[localStyles.tableCellDesc, {flex: 1.4}, line.bold && {fontWeight: '700'}]}>
              {line.label}
            </Text>
            {rows.map(r => {
              const v = line.value ? line.value(r) : num(r[line.key]);
              const color =
                line.key === 'rp_net' ? (v >= 0 ? colors.success : colors.error) : line.color;
              return (
                <Text
                  key={r.rp_period}
                  style={[localStyles.tableCellAmount, localStyles.textRight, color ? {color} : null]}>
                  {Math.round(v).toLocaleString()}
                </Text>
              );
            })}
          </View>
        ))}
        <View style={localStyles.tableRow}>
          <Text style={[localStyles.tableCellDesc, {flex: 1.4, fontWeight: '700'}]}><T>Cost %</T></Text>
          {rows.map(r => (
            <Text key={r.rp_period} style={[localStyles.tableCellAmount, localStyles.textRight]}>
              {r.rp_costpct === null || r.rp_costpct === undefined
                ? '—'
                : `${Number(r.rp_costpct).toFixed(1)}%`}
            </Text>
          ))}
        </View>
        {flagged === 0 && (
          <Text style={[localStyles.tableCellDesc, {marginTop: spacing.sm}]}>
            <T>No departments are marked Restaurant / Kitchen, so store issues are not counted. Mark them in
            the Department master (web).</T>
          </Text>
        )}
      </View>
    </>
  );
};

const Wallets = ({date}: {date: Date}) => {
  const navigation = useNavigation();
  const gdata = useSelector((state: RootState) => state.gdata);
  const [totSales, setTotSales] = useState(0);
  const [FO, setFO] = useState(0);
  const [POS, setPOS] = useState(0);
  const [HALL, setHALL] = useState(0);
  const [OTH, setOTH] = useState(0);
  const [PUR, setPUR] = useState(0);
  const [EXP, setEXP] = useState(0);
  const [PAY, setPAY] = useState(0);
  const [SAL, setSAL] = useState(0);

  const {data} = useGetDashboardsQuery({
    cid: gdata.company,
    dt: moment(date).utcOffset('+05:30').format(),
  });

  // Restaurant income vs expense - POS module properties only
  const modPOS = gdata.menu?.some((v: any) => Number(v.mn_module) === 3);
  const {data: restaurantData} = useGetDashboardRestaurantQuery(
    {
      cid: gdata.company,
      dt: moment(date).utcOffset('+05:30').format(),
      mode: 'W',
    },
    {skip: !modPOS},
  );

  const {data: occData} = useGetFOECheckinOccByDateQuery({
    cid: gdata.company,
    fdt: moment(date).utcOffset('+05:30').format(),
    tdt: moment(date).utcOffset('+05:30').format(),
  });

  const summ = occData?.oc_summ[0];

  const isToday = moment(date).isSame(moment(), 'day');

  const {data: highBalanceData, isFetching: isHighBalanceFetching} = useGetFOHighBalanceQuery(
    {
      cid: gdata.company,
      fdt: moment(date).format('YYYY-MM-DD'),
      tdt: moment(date).format('YYYY-MM-DD'),
    },
    {skip: !isToday || !gdata.company},
  );

  const highBalanceRows = useMemo(() => {
    const rows = Array.isArray(highBalanceData) ? highBalanceData : [];
    return rows
      .filter((r: any) => Number(r.ci_bal) > 0)
      .map((r: any) => ({
        guestName: r.ci_name || 'Unknown',
        room: r.ci_room || '-',
        balance: Number(r.ci_bal || 0),
      }))
      .sort((a: {balance: number}, b: {balance: number}) => b.balance - a.balance);
  }, [highBalanceData]);

  useEffect(() => {
    let totSales1 = 0;
    let FO1 = 0,
      POS1 = 0,
      HALL1 = 0,
      OTH1 = 0,
      PUR1 = 0,
      EXP1 = 0,
      PAY1 = 0,
      SAL1 = 0;

    data?.[0]?.forEach((v: any) => {
      totSales1 += Number(v.db_amt);
      if (v.db_name === '1. FO') FO1 += Number(v.db_amt);
      else if (v.db_name === '2. POS') POS1 += Number(v.db_amt);
      else if (v.db_name === '3. HALL') HALL1 += Number(v.db_amt);
      else if (v.db_name === '4. HK' || v.db_name === '5. OTH')
        OTH1 += Number(v.db_amt);
      else if (v.db_name === '6. Pur') PUR1 += Number(v.db_amt);
      else if (v.db_name === '7. Exp') EXP1 += Number(v.db_amt);
      else if (v.db_name === '8. Pay') PAY1 += Number(v.db_amt);
      else if (v.db_name === '9. Sal') SAL1 += Number(v.db_amt);
      else OTH1 += Number(v.db_amt);
    });

    setTotSales(totSales1);
    setFO(FO1);
    setPOS(POS1);
    setHALL(HALL1);
    setOTH(OTH1);
    setPUR(PUR1);
    setEXP(EXP1);
    setPAY(PAY1);
    setSAL(SAL1);
  }, [data]);

  const SummaryCard: React.FC<{title: string; data: any[]}> = ({
    title,
    data: tableData,
  }) => {
    let totCr = 0;
    let totDr = 0;

    return (
      <View style={localStyles.summaryCard}>
        <View style={localStyles.summaryHeader}>
          <View style={[localStyles.headerIconBadge, {backgroundColor: colors.successLight}]}>
            <FontAwesome name="line-chart" size={16} color={colors.success} />
          </View>
          <Text style={localStyles.summaryTitle}>{title} <T>Financial Summary</T></Text>
        </View>

        {/* Table Column Headers */}
        <View style={localStyles.tableHeaderRow}>
          <Text style={[localStyles.tableHeadCell, {flex: 2}]}><T>Description</T></Text>
          <Text style={[localStyles.tableHeadCell, localStyles.textRight]}><T>Credit (₹)</T></Text>
          <Text style={[localStyles.tableHeadCell, localStyles.textRight]}><T>Debit (₹)</T></Text>
        </View>

        {tableData?.map((val, i) => {
          totCr += Number(val.mi_cr);
          totDr += Number(val.mi_dr);
          return (
            <View
              style={[
                localStyles.tableRow,
                i % 2 === 1 && localStyles.tableRowAlt,
              ]}
              key={`${title}-${i}`}>
              <Text style={[localStyles.tableCellDesc, {flex: 2}]}>
                {val.mi_descr}
              </Text>
              <Text style={[localStyles.tableCellAmount, localStyles.textRight, {color: colors.success}]}>
                {Number(val.mi_cr) > 0 ? Number(val.mi_cr).toLocaleString() : '-'}
              </Text>
              <Text style={[localStyles.tableCellAmount, localStyles.textRight, {color: colors.error}]}>
                {Number(val.mi_dr) > 0 ? Number(val.mi_dr).toLocaleString() : '-'}
              </Text>
            </View>
          );
        })}

        {/* Totals */}
        <View style={localStyles.tableTotalRow}>
          <Text style={[localStyles.totalLabel, {flex: 2}]}><T>Total</T></Text>
          <Text style={[localStyles.totalValue, localStyles.textRight, {color: colors.success}]}>
            ₹{totCr.toLocaleString()}
          </Text>
          <Text style={[localStyles.totalValue, localStyles.textRight, {color: colors.error}]}>
            ₹{totDr.toLocaleString()}
          </Text>
        </View>

        {/* Balance */}
        <View style={localStyles.balanceRow}>
          <Text style={localStyles.balanceLabel}><T>Net Balance</T></Text>
          <View style={localStyles.balancePill}>
            <Text style={localStyles.balanceValue}>
              ₹{(totCr - totDr).toLocaleString()}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const occupancyItems = [
    {
      label: "Today's Occupancy",
      value: summ?.oc_occ,
      icon: 'bed',
      bgColor: '#fff7ed',
      iconColor: '#f97316',
    },
    {
      label: 'Day Use',
      value: summ?.oc_du,
      icon: 'sun-o',
      bgColor: '#fefce8',
      iconColor: '#eab308',
    },
    {
      label: 'Expected Checkout',
      value: summ?.oc_eco,
      icon: 'sign-out',
      bgColor: '#fef2f2',
      iconColor: '#ef4444',
    },
    {
      label: 'Expected Checkin',
      value: summ?.oc_eci,
      icon: 'sign-in',
      bgColor: '#eff6ff',
      iconColor: '#3b82f6',
    },
    {
      label: 'Vacant',
      value: summ?.oc_vac,
      icon: 'unlock',
      bgColor: '#f0fdf4',
      iconColor: '#10b981',
    },
    {
      label: 'Dirty',
      value: summ?.oc_dir,
      icon: 'trash',
      bgColor: '#f8fafc',
      iconColor: '#64748b',
    },
    {
      label: 'Blocked',
      value: summ?.oc_blk,
      icon: 'ban',
      bgColor: '#fff1f2',
      iconColor: '#f43f5e',
    },
    {
      label: 'Maintenance',
      value: summ?.oc_mnt,
      icon: 'wrench',
      bgColor: '#f3e8ff',
      iconColor: '#8b5cf6',
    },
  ];

  const salesItems = [
    {label: 'Total Sales', value: totSales, icon: 'money', color: colors.success},
    {label: 'Front Office', value: FO, icon: 'hotel', color: colors.primary},
    {label: 'POS Sales', value: POS, icon: 'shopping-cart', color: colors.warning},
    {label: 'Hall Sales', value: HALL, icon: 'building', color: '#a855f7'},
    {label: 'Others', value: OTH, icon: 'credit-card', color: colors.info},
    {label: 'Purchases', value: PUR, icon: 'shopping-bag', color: '#f97316'},
    {label: 'Expenses', value: EXP, icon: 'tags', color: colors.error},
    {label: 'Payments', value: PAY, icon: 'check-circle', color: colors.success},
    {label: 'Salaries', value: SAL, icon: 'users', color: '#8b5cf6'},
  ];

  return (
    <ScrollView style={localStyles.scrollContainer} keyboardShouldPersistTaps="handled">
      {/* Occupancy Details Section Header */}
      <View style={localStyles.sectionHeader}>
        <View style={[localStyles.headerIconBadge, {backgroundColor: '#ffedd5'}]}>
          <FontAwesome name="bed" size={16} color="#f97316" />
        </View>
        <Text style={localStyles.sectionTitle}><T>Occupancy Overview</T></Text>
      </View>

      <View style={localStyles.gridContainer}>
        {occupancyItems.map((item, index) => (
          <View key={index} style={[localStyles.occCard, {backgroundColor: colors.white}]}>
            <View style={[localStyles.occIconBadge, {backgroundColor: item.bgColor}]}>
              <FontAwesome name={item.icon as any} size={18} color={item.iconColor} />
            </View>
            <View style={localStyles.occContent}>
              <Text style={localStyles.occLabel}>{item.label}</Text>
              <Text style={[localStyles.occCount, {color: item.iconColor}]}>
                {item.value ?? 0}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {/* Financial Sales Summary Section */}
      <View style={localStyles.sectionHeader}>
        <View style={[localStyles.headerIconBadge, {backgroundColor: colors.successLight}]}>
          <FontAwesome name="bar-chart" size={16} color={colors.success} />
        </View>
        <Text style={localStyles.sectionTitle}><T>Financial Breakdown</T></Text>
      </View>

      <View style={localStyles.gridContainer}>
        {salesItems.map((item, index) => (
          <View key={index} style={localStyles.salesCard}>
            <View style={localStyles.salesCardHeader}>
              <View style={[localStyles.salesIconBadge, {backgroundColor: `${item.color}15`}]}>
                <FontAwesome name={item.icon as any} size={16} color={item.color} />
              </View>
              <Text style={localStyles.salesLabel}>{item.label}</Text>
            </View>
            <Text style={[localStyles.salesValue, {color: item.color}]}>
              ₹{item.value.toLocaleString()}
            </Text>
          </View>
        ))}
      </View>

      {/* Restaurant Income vs Expense */}
      {modPOS && restaurantData && restaurantData.length > 0 && (
        <RestaurantPnLCard rows={restaurantData} />
      )}

      {/* High Balance Guests Section */}
      <View style={localStyles.sectionHeader}>
        <View style={[localStyles.headerIconBadge, {backgroundColor: colors.errorLight}]}>
          <FontAwesome name="exclamation-triangle" size={16} color={colors.error} />
        </View>
        <Text style={localStyles.sectionTitle}><T>High Balance Guests</T></Text>
      </View>

      <View style={localStyles.summaryCard}>
        {!isToday ? (
          <Text style={localStyles.tableCellDesc}><T>Available for today only</T></Text>
        ) : isHighBalanceFetching ? (
          <Text style={localStyles.tableCellDesc}><T>Loading…</T></Text>
        ) : highBalanceRows.length === 0 ? (
          <Text style={localStyles.tableCellDesc}><T>No outstanding dues</T></Text>
        ) : (
          <>
            <View style={localStyles.tableHeaderRow}>
              <Text style={[localStyles.tableHeadCell, {flex: 2}]}><T>Guest</T></Text>
              <Text style={[localStyles.tableHeadCell, {flex: 1}]}><T>Room</T></Text>
              <Text style={[localStyles.tableHeadCell, localStyles.textRight]}><T>Balance (₹)</T></Text>
            </View>
            {highBalanceRows.map((row, i) => (
              <View
                key={`${row.guestName}-${row.room}-${i}`}
                style={[localStyles.tableRow, i % 2 === 1 && localStyles.tableRowAlt]}>
                <Text style={[localStyles.tableCellDesc, {flex: 2}]}>{row.guestName}</Text>
                <Text style={[localStyles.tableCellDesc, {flex: 1}]}>{row.room}</Text>
                <Text style={[localStyles.tableCellAmount, localStyles.textRight, {color: colors.error}]}>
                  {row.balance.toLocaleString()}
                </Text>
              </View>
            ))}
          </>
        )}
      </View>

      {/* Credit & Debit Summaries */}
      <View style={localStyles.summaryCardContainer}>
        <SummaryCard title="Today" data={data?.[1]} />
        <SummaryCard title="This Week" data={data?.[2]} />
        <SummaryCard title="This Month" data={data?.[3]} />
      </View>
    </ScrollView>
  );
};

const localStyles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
    marginVertical: spacing.xs,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.md,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  headerIconBadge: {
    width: 32,
    height: 32,
    borderRadius: borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
  },
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  occCard: {
    width: '48%',
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    borderRadius: borderRadius.xl,
    ...shadows.md,
    borderWidth: 1,
    borderColor: colors.borderLight,
  },
  occIconBadge: {
    width: 40,
    height: 40,
    borderRadius: borderRadius.lg,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  occContent: {
    flex: 1,
  },
  occLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    fontWeight: typography.fontWeight.medium,
  },
  occCount: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    marginTop: 2,
  },
  salesCard: {
    width: '31%',
    minWidth: 105,
    backgroundColor: colors.white,
    borderRadius: borderRadius.xl,
    padding: spacing.md,
    ...shadows.md,
    borderWidth: 1,
    borderColor: colors.borderLight,
  },
  salesCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  salesIconBadge: {
    width: 28,
    height: 28,
    borderRadius: borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.xs,
  },
  salesLabel: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textSecondary,
    flex: 1,
  },
  salesValue: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
    marginTop: spacing.xs,
  },
  summaryCardContainer: {
    marginTop: spacing.md,
  },
  summaryCard: {
    backgroundColor: colors.white,
    borderRadius: borderRadius['2xl'],
    padding: spacing.lg,
    marginVertical: spacing.sm,
    ...shadows.lg,
    borderWidth: 1,
    borderColor: colors.borderLight,
  },
  summaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  summaryTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: colors.backgroundSecondary,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: borderRadius.md,
    marginBottom: spacing.xs,
  },
  tableHeadCell: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.bold,
    color: colors.textSecondary,
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  tableRowAlt: {
    backgroundColor: colors.backgroundSecondary,
  },
  tableCellDesc: {
    fontSize: typography.fontSize.sm,
    color: colors.textPrimary,
    fontWeight: typography.fontWeight.medium,
  },
  tableCellAmount: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
  },
  tableTotalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
    borderTopWidth: 2,
    borderTopColor: colors.borderMedium,
    marginTop: spacing.xs,
  },
  totalLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
  },
  totalValue: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
  },
  balanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.lightGradient[0],
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: borderRadius.xl,
    marginTop: spacing.sm,
  },
  balanceLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
  },
  balancePill: {
    backgroundColor: colors.accent,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.full,
    ...shadows.colored,
  },
  balanceValue: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
  },
  textRight: {
    textAlign: 'right',
  },
});

export default Wallets;
